import * as React from "react";
import { useCallback, useState } from "react";
import { Download as DownloadIcon } from "@mui/icons-material";
import {
  Button,
  defaultExporter,
  fetchRelatedRecords,
  useDataProvider,
  useListContext,
  useNotify,
  type ExportButtonProps,
} from "react-admin";

import { fetchListAll } from "@/providers/dataProvider/fetchListAll";
import { EXPORT_MAX_RECORDS } from "@/providers/dataProvider/paginationLimits";

/**
 * Drop-in replacement for react-admin's <ExportButton> that fetches in
 * server-sized page chunks instead of one giant perPage request (#5166 — the
 * mgmt-api caps a single page at 1000 with coronis-apps #5166, and a single 50k
 * pull OOM-killed Aura's prod API, ADO 5702/5703). Honors the view's current
 * filter/sort and the List-configured exporter, disables itself while a bulk
 * run is in flight (50k = up to 50 sequential requests), and warns the user when
 * the export is truncated.
 *
 * Truncated exports also get a marker row appended to the file. That marker is a
 * synthetic record whose only populated field is the first data key — safe with
 * `defaultExporter` (jsonexport), which all wired lists use. A future List-level
 * exporter that maps strongly-typed columns would receive a row with one string
 * field and blanks elsewhere; revisit the marker shape if that lands.
 */
export const ChunkedExportButton = React.forwardRef<HTMLButtonElement, ExportButtonProps>(
  function ChunkedExportButton(props, ref) {
    const {
      maxResults = EXPORT_MAX_RECORDS,
      onClick,
      label = "Export",
      icon = defaultIcon,
      exporter: customExporter,
      meta,
      disabled,
      ...rest
    } = props;
    const { filter, filterValues, resource, sort, exporter: exporterFromContext, total } = useListContext();
    const exporter = customExporter || exporterFromContext || defaultExporter;
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [isExporting, setIsExporting] = useState(false);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        // A bulk export is up to 50 sequential requests (~30-60s). Guard against
        // re-entrant clicks stacking parallel request chains, and give feedback.
        if (isExporting) return;
        setIsExporting(true);
        notify(total ? `Exporting ${total} records…` : "Exporting records…", { type: "info" });
        // Deliberately NOT using listContext.getData — it fetches a single
        // perPage=maxResults page, the exact pattern this button removes.
        fetchListAll(
          dataProvider,
          resource,
          {
            sort,
            filter: filter ? { ...filterValues, ...filter } : filterValues,
            meta,
          },
          {
            maxRecords: maxResults,
            onTruncated: (fetched, totalCount) =>
              notify(`Export limited to first ${fetched} of ${totalCount} records`, { type: "warning" }),
          }
        )
          .then(({ data, total: totalCount, truncated }) => {
            if (!exporter) return;
            // The toast is dismissable; the file itself must carry the marker
            // so a cut export can't be reconciled as complete. The marker reuses
            // the first data key so it lands in the first column without adding
            // a phantom column.
            let rows = data;
            if (truncated && data.length > 0) {
              const firstKey = Object.keys(data[0])[0];
              rows = [
                ...data,
                {
                  [firstKey]: `⚠ TRUNCATED EXPORT: first ${data.length} of ${totalCount} rows — re-run with narrower filters`,
                } as (typeof data)[number],
              ];
            }
            return exporter(rows, fetchRelatedRecords(dataProvider), dataProvider, resource);
          })
          .catch((error) => {
            console.error(error);
            notify("ra.notification.http_error", { type: "error" });
          })
          .finally(() => setIsExporting(false));
        if (typeof onClick === "function") {
          onClick(event);
        }
      },
      [
        dataProvider,
        exporter,
        filter,
        filterValues,
        isExporting,
        maxResults,
        meta,
        notify,
        onClick,
        resource,
        sort,
        total,
      ]
    );

    return (
      <Button ref={ref} onClick={handleClick} label={label} disabled={disabled || isExporting || total === 0} {...rest}>
        {icon}
      </Button>
    );
  }
);

const defaultIcon = <DownloadIcon />;
